import { useQuery } from '@tanstack/react-query';
import { TeamProject } from '@/types/TeamProject';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook to get team projects where the current user is a member (not creator)
 * This is useful for team members to see only their assigned projects
 */
export function useMyTeamProjects() {
  return useQuery({
    queryKey: ['my-team-projects'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('team_projects' as any)
        .select(`
          *,
          team_project_members!inner(
            role,
            user_id
          )
        `)
        .eq('team_project_members.user_id', user.id)
        .neq('created_by', user.id) // Exclude projects where user is the creator
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get member counts for each project
      const projectsWithMemberCount = await Promise.all(
        (data || []).map(async (project: any) => {
          const { data: memberCount, error: countError } = await supabase
            .from('team_project_members' as any)
            .select('id', { count: 'exact' })
            .eq('project_id', project.id);

          if (countError) throw countError;

          // Get user's role in the project
          const userMember = project.team_project_members?.find((m: any) => m.user_id === user.id);
          
          return {
            ...project,
            member_count: memberCount?.length || 0,
            user_role: userMember?.role || 'member'
          } as TeamProject;
        })
      );

      return projectsWithMemberCount;
    },
  });
}

/**
 * Hook to get team projects where the current user is the creator/owner
 */
export function useOwnedTeamProjects() {
  return useQuery({
    queryKey: ['owned-team-projects'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('team_projects' as any)
        .select(`
          *,
          team_project_members(
            role,
            user_id
          )
        `)
        .eq('created_by', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get member counts for each project
      const projectsWithMemberCount = (data || []).map((project: any) => ({
        ...project,
        member_count: project.team_project_members?.length || 0,
        user_role: 'owner'
      } as TeamProject));

      return projectsWithMemberCount;
    },
  });
}